/**
 * TanStack Query hooks for the caller's family.
 *
 * Hooks intentionally DO NOT read family data from the `/me` endpoint
 * cache — they query `/api/families/mine` directly with a separate
 * cache key (`["family","mine"]`). This keeps the two concerns
 * cleanly separated:
 *
 *   - `useAuth` → "who am I right now?" (fast, every page)
 *   - `useFamily` → "what does the family settings page need?"
 *
 * The create and rename mutations invalidate BOTH the `["auth","me"]`
 * cache (so the top bar + dashboard see the update) and the
 * `["family","mine"]` cache (so the settings page refreshes).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/api";
import { AUTH_QUERY_KEY, type Family } from "@/features/auth/useAuth";

const FAMILY_QUERY_KEY = ["family", "mine"] as const;

export function useFamily() {
  return useQuery<Family | null, Error>({
    queryKey: FAMILY_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<Family>("/api/families/mine");
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          // 404 = "user has no family" — valid state, not an error
          return null;
        }
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

export function useCreateFamily() {
  const queryClient = useQueryClient();
  return useMutation<Family, Error, { name: string }>({
    mutationFn: ({ name }) =>
      apiFetch<Family>("/api/families", {
        method: "POST",
        body: { name },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FAMILY_QUERY_KEY });
    },
  });
}

export function useRenameFamily() {
  const queryClient = useQueryClient();
  return useMutation<Family, Error, { name: string }>({
    mutationFn: ({ name }) =>
      apiFetch<Family>("/api/families/mine", {
        method: "PATCH",
        body: { name },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FAMILY_QUERY_KEY });
    },
  });
}

interface FamilyContextInput {
  location?: string | null | undefined;
  budget_level?: string | null | undefined;
  interests?: string | null | undefined;
}

export function useUpdateFamilyContext() {
  const queryClient = useQueryClient();
  return useMutation<Family, Error, FamilyContextInput>({
    mutationFn: (input) =>
      apiFetch<Family>("/api/families/mine/context", {
        method: "PATCH",
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FAMILY_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
}

export { FAMILY_QUERY_KEY };
